import React, {
    ChangeEvent,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    signupWithInvitation,
    individualSignUp,
    userExists,
} from '../../services/apiService';
import {
    emailValidate,
    nameValidate,
    passwordValidate,
    repeatPasswordValidate,
} from '../../services/formValidationService';
import Button, { ButtonType } from '../general-components/Button';
import SignupLayout from '../ui-components/SignupLayout';
import styles from './SignupForm.module.css';
import sanitizeHtml from 'sanitize-html';
import {
    formReducer,
    errorReducer,
    camelCaseToSentence,
} from '../../utils/utils';
import HospitalInfoForm from './HospitalInfo';
import UserInfoForm from './UserInfo';
import VariantSelect from './VariantSelect';
import PaddleCheckout from '../subscription/PaddleCheckout';
import SignupContext from '../../store/signup-context';
import SelectItem from '../general-components/SelectItem';

const SignupForm: React.FC = () => {
    const { t } = useTranslation();
    const {
        formData,
        isIndividual,
        validateFields,
        setCurrentElement,
        id,
        step,
        setStep,
    } = useContext(SignupContext);

    const [signupStatus, setSignupStatus] = useState<string>();

    const [generalError, setGeneralError] = useState('');

    const [submitLoading, setSubmitLoading] = useState(false);

    const [acceptTerms, setAcceptTerms] = useState(false);
    const [termsError, setTermsError] = useState('');
    const [variantId, setVariantId] = useState<string>();

    const selectVariant = (v: string) => {
        setVariantId(v);
        setStep(2);
    };

    const handlePaddleClose = (paddleStatus: string) => {
        if (paddleStatus === 'completed') {
            setCurrentElement('confirm');
        }
    };

    const submitIndividualSignup = async () => {
        let canOpen = false;
        let customerId = '';
        let email = '';
        if (!acceptTerms) {
            setTermsError(t('signup.signupForm.terms.error'));
        } else if (validateFields(isIndividual)) {
            setSubmitLoading(true);
            const result = await individualSignUp(formData);
            setSignupStatus(result.status);
            if (result.status === 'success') {
                canOpen = true;
                email = formData.email;
            } else if (result.status === 'incomplete_individual') {
                canOpen = true;
                customerId = result.customerId;
            } else {
                setGeneralError(
                    t('signup.signupForm.errors.subscriptionExists')
                );
            }
        }
        return {
            canOpen,
            customerId,
            email,
        };
    };

    const submitInvitationSignup = async () => {
        if (!acceptTerms) {
            setTermsError(t('signup.signupForm.terms.error'));
        } else if (validateFields(isIndividual)) {
            setSubmitLoading(true);

            const result = await signupWithInvitation(formData, id);
            if (result.success) {
                setCurrentElement('confirm');
            } else {
                setSubmitLoading(false);
                setGeneralError(
                    typeof result?.message === 'string' && result.message
                        ? result.message
                        : t('signup.signupForm.errors.generic')
                );
            }
        }
    };

    return (
        <SignupLayout>
            <React.Fragment>
                {step === 2 && !isIndividual && (
                    <div className={styles.info}>
                        <h3>{t('signup.signupForm.title')}</h3>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(
                                    t('signup.signupForm.description')
                                ),
                            }}
                        ></p>
                    </div>
                )}
                {step === 2 && isIndividual && (
                    <div className={styles.info}>
                        <h1>{t('signup.signupForm.createUser.title')}</h1>
                        {/* <p>Please setup a password of atleast 8 characters.</p> */}
                    </div>
                )}
                {step === 1 && isIndividual && (
                    <div className={styles.info}>
                        <h1>{t('signup.signupForm.selectBilling.title')}</h1>
                        <p>
                            {t('signup.signupForm.selectBilling.description')}
                        </p>
                    </div>
                )}
                <form className={styles.form} autoComplete="off">
                    <span className={styles.error} aria-live="polite">
                        {generalError}
                    </span>
                    {step === 1 && isIndividual && (
                        <VariantSelect onSelect={selectVariant} />
                    )}
                    {step === 2 && (
                        <>
                            <UserInfoForm />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <div className={styles.checkbox}>
                                    <SelectItem
                                        labelled={false}
                                        type="checkbox"
                                        value="accept"
                                        error={termsError !== ''}
                                        onChange={() => {
                                            setAcceptTerms(!acceptTerms);
                                            setTermsError('');
                                        }}
                                    />
                                    <label>
                                        <Trans i18nKey="signup.signupForm.terms.help">
                                            I accept the
                                            <a
                                                target="__blank"
                                                href="https://nordinsight.notion.site/terms-conditions"
                                            >
                                                terms of service
                                            </a>
                                        </Trans>
                                    </label>
                                </div>
                                <span
                                    className={styles.error}
                                    aria-live="polite"
                                >
                                    {termsError}
                                </span>
                            </div>
                        </>
                    )}
                    {step === 2 && !isIndividual && (
                        <Button
                            className={styles.button}
                            type={ButtonType.submit}
                            onClick={() => {
                                submitInvitationSignup();
                            }}
                            loading={submitLoading}
                        >
                            {t('signup.signupForm.buttons.signup')}
                        </Button>
                    )}
                    {step === 2 && isIndividual && (
                        <PaddleCheckout
                            priceId={variantId}
                            onClose={handlePaddleClose}
                            beforeOpen={submitIndividualSignup}
                        >
                            {t('signup.signupForm.buttons.signup')}
                        </PaddleCheckout>
                    )}
                </form>
            </React.Fragment>
        </SignupLayout>
    );
};

export default SignupForm;
